import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { api } from '../../../lib/api';
import { Class } from '../types';

interface ClassCardProps {
  classData: Class;
}

type ClassroomStats = {
  classroom_id: number;
  classroom_name: string;
  students_count: number;
  coordinator: { id: number | null; full_name: string | null; email: string | null };
  avg_grade: number | null;
  rating_percent: number | null;
  rating_percent_prev?: number | null;
  rating_delta?: number | null;
  rating_trend?: 'up' | 'down' | 'flat' | null;
  rating_trend_window_days?: number | null;
  top_rank: number | null;
  top_message: string;
};

const ClassCard = ({ classData }: ClassCardProps) => {
  const color = classData.color || '#2563EB';

  const [localStats, setLocalStats] = useState<ClassroomStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  const effective = useMemo(() => {
    const coordinatorFullName = localStats?.coordinator?.full_name ?? classData.coordinatorName ?? classData.teacher ?? null;
    const coordinatorEmail = localStats?.coordinator?.email ?? classData.coordinatorEmail ?? null;
    const studentsCount = localStats?.students_count ?? classData.studentsCount ?? null;
    const ratingPercent = localStats?.rating_percent ?? classData.classRatingPercent ?? null;
    const ratingPercentPrev = localStats?.rating_percent_prev ?? classData.classRatingPercentPrev ?? null;
    const ratingDelta = localStats?.rating_delta ?? classData.classRatingDelta ?? null;
    const ratingTrend = localStats?.rating_trend ?? classData.classRatingTrend ?? null;
    const ratingTrendWindowDays = localStats?.rating_trend_window_days ?? classData.classRatingTrendWindowDays ?? null;
    const topRank = localStats?.top_rank ?? classData.topRank ?? null;
    const topMessage = localStats?.top_message ?? classData.topMessage ?? 'Lucrați mai mult pentru a fi în top 5.';

    return {
      coordinatorFullName,
      coordinatorEmail,
      studentsCount,
      ratingPercent,
      ratingPercentPrev,
      ratingDelta,
      ratingTrend,
      ratingTrendWindowDays,
      topRank,
      topMessage,
    };
  }, [
    classData.classRatingPercent,
    classData.classRatingDelta,
    classData.classRatingPercentPrev,
    classData.classRatingTrend,
    classData.classRatingTrendWindowDays,
    classData.coordinatorEmail,
    classData.coordinatorName,
    classData.studentsCount,
    classData.teacher,
    classData.topMessage,
    classData.topRank,
    localStats,
  ]);

  const loadStats = async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const resp = await api.get(`/api/scoli/classrooms/${encodeURIComponent(classData.id)}/stats`);
      setLocalStats(resp.data as ClassroomStats);
    } catch (err: any) {
      setLocalStats(null);
      setStatsError(err?.response?.data?.detail || 'Nu s-au putut încărca detaliile clasei.');
    } finally {
      setStatsLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
            <Icon name="BookOpen" size={24} color={color} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{classData.name}</h3>
            <p className="text-sm text-muted-foreground">{classData.subject || '—'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadStats}
            disabled={statsLoading}
            iconName="RefreshCcw"
            iconPosition="left"
          >
            Reîncarcă
          </Button>
          {classData.isOnline && classData.nextClass && (
            <span className="flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-md">
              <Icon name="Video" size={12} />
              Live
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full overflow-hidden bg-muted">
          {classData.teacherAvatar ? (
            <Image
              src={classData.teacherAvatar}
              alt={effective.coordinatorFullName ? `Fotografie profil: ${effective.coordinatorFullName}` : 'Profesor'}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground text-xs font-medium">
              {(effective.coordinatorFullName || classData.teacher || 'P').charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <div className="text-sm text-muted-foreground truncate">
            Profesor coordonator: {effective.coordinatorFullName || '—'}
          </div>
          <div className="text-xs text-muted-foreground truncate">{effective.coordinatorEmail || '—'}</div>
        </div>
      </div>

      {statsError && (
        <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm mb-4">{statsError}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="border border-border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Număr elevi</div>
          <div className="text-lg font-semibold text-foreground">{statsLoading ? '...' : effective.studentsCount ?? '—'}</div>
        </div>
        <div className="border border-border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Rating clasă</div>
          <div className="text-lg font-semibold text-foreground flex items-center gap-2">
            <span>{statsLoading ? '...' : typeof effective.ratingPercent === 'number' ? `${effective.ratingPercent}%` : '—'}</span>
            {!statsLoading && typeof effective.ratingPercent === 'number' && (
              <span
                className="inline-flex items-center"
                title={
                  typeof effective.ratingDelta === 'number'
                    ? (effective.ratingTrend || 'flat') === 'up'
                      ? `În urcare (+${effective.ratingDelta} pp, ultimele ${effective.ratingTrendWindowDays ?? 30} zile)`
                      : (effective.ratingTrend || 'flat') === 'down'
                        ? `În coborâre (${effective.ratingDelta} pp, ultimele ${effective.ratingTrendWindowDays ?? 30} zile)`
                        : `Stagnare (0 pp, ultimele ${effective.ratingTrendWindowDays ?? 30} zile)`
                    : `Nu există date anterioare pentru comparație (ultimele ${effective.ratingTrendWindowDays ?? 30} zile).`
                }
              >
                <Icon
                  name={(effective.ratingTrend || 'flat') === 'up' ? 'TrendingUp' : (effective.ratingTrend || 'flat') === 'down' ? 'TrendingDown' : 'Minus'}
                  size={16}
                  className={
                    typeof effective.ratingDelta === 'number'
                      ? (effective.ratingTrend || 'flat') === 'up'
                        ? 'text-success'
                        : (effective.ratingTrend || 'flat') === 'down'
                          ? 'text-error'
                          : 'text-muted-foreground'
                      : 'text-muted-foreground'
                  }
                />
              </span>
            )}
          </div>
        </div>
        <div className="border border-border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Top școală</div>
          <div className="text-sm font-medium text-foreground">
            {statsLoading ? '...' : typeof effective.topRank === 'number' ? `Locul ${effective.topRank} (Top 5)` : effective.topMessage}
          </div>
        </div>
      </div>

      {(classData.nextClass || classData.location) && (
        <div className="space-y-2">
          {classData.nextClass && (
            <div className="flex items-center gap-2 text-sm">
              <Icon name="Clock" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">Următoarea oră: {classData.nextClass}</span>
            </div>
          )}
          {classData.location && (
            <div className="flex items-center gap-2 text-sm">
              <Icon name="MapPin" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">{classData.location}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClassCard;
